import ELightsThreeTypeEnum from '@/commons/lights/typescript/enum/ELightsThreeTypeEnum';
import CustomLightPresets from '@/commons/lights/CustomLightPresets';
import { AmbientLight, DirectionalLight, Object3D, SpotLight, Color } from 'three';
import type { ILightThreeParams } from '@/commons/lights/typescript/interface/ILightThreeParams';
import type { ILightPreset } from '@/commons/lights/typescript/interface/ILightPreset';

/**
 * Return a directionnal light with a target already set in [0, 0, 0]
 * @param LightParams
 * @returns {DirectionalLight}

 */

function getDirectionalLight(LightParams: ILightThreeParams | null): DirectionalLight {
    if (!LightParams) {
        return new DirectionalLight();
    }
    // TODO: use angle to position light on a sphere.
    const { color /* , power */ } = LightParams;
    const target = new Object3D();
    target.name = 'dummy';
    const light = new DirectionalLight(color);
    light.target = target; // target is already setted but can be change by the user
    light.castShadow = true;
    return light;
}

/**
 * Return a directionnal light with a target already set in [0, 0, 0]
 * @param LightParams
 * @returns { SpotLight }
 */
function getSpotLight(params: ILightThreeParams | null): SpotLight {
    if (!params) {
        return new SpotLight();
    }
    const { angle, color, power } = params;
    const light = new SpotLight(color, 1, 0, angle);
    light.power = power;
    light.castShadow = true;
    return light;
}

/**
 * Return a directionnal light with a target already set in [0, 0, 0]
 * @param AmbientLight
 * @returns { AmbientLight }
 */
function getAmbientLight(params: ILightThreeParams | null): AmbientLight {
    if (!params) {
        return new AmbientLight(new Color(0.94, 0.87, 0.75), 20);
    }
    const {
        color: [r, g, b],
        power,
    } = params;
    return new AmbientLight(new Color(r, g, b), power);
}

/* ------------------ Fetching Presets from API ------------------ */
function getThreeParamsFromPresets(lightPreset: ILightPreset): ILightThreeParams {
    const lightColor = new Color(lightPreset.color[0], lightPreset.color[1], lightPreset.color[2]);
    const threeLightType = ELightsThreeTypeEnum[lightPreset.type[0] as unknown as keyof typeof ELightsThreeTypeEnum];
    return {
        angle: lightPreset?.angle ?? 45,
        color: lightColor,
        power: lightPreset.intensity,
        temperature: lightPreset.temperature,
        type: threeLightType,
    };
}

function getLightsPresetsSortByTypes() {
    const presets = fetchLightsPresets();
    const allTypePromise: Promise<string[]> = presets.then((settings: ILightPreset[]) => {
        // assuming type is oànly one value
        return Array.from(new Set(settings.map((set: ILightPreset) => set.type[0])));
    });
    return allTypePromise.then((typeArray) => {
        const promiseArray: Promise<Record<string, ILightPreset[]>>[] = [];
        typeArray.forEach((type) => {
            const presetSortByType: Record<string, ILightPreset[]> = {};
            promiseArray.push(
                presets
                    .then((settings) => {
                        const elementTypeArray = settings.filter((set: ILightPreset) => set.type[0] === type);
                        if (elementTypeArray) {
                            presetSortByType[type] = elementTypeArray;
                        }
                    })
                    .then(() => {
                        return presetSortByType;
                    }),
            );
        });
        return Promise.all(promiseArray).then((result) => {
            const lightSortByType: Record<string, ILightPreset[]> = {};
            result.forEach((presetSortByType) => {
                Object.assign(lightSortByType, presetSortByType, CustomLightPresets);
            });
            // FIXME: add all sort of preset ligth not only spot and directional lights
            return {
                spot: lightSortByType.spot,
                directional: lightSortByType.directional,
                ambient: lightSortByType.ambient,
            };
        });
    });
}

async function fetchLightsPresets() {
    const url = 'https://api.physicallybased.info/lightsources';
    const options = { method: 'GET', headers: { Accept: 'application/json' } };

    try {
        const response = await fetch(url, options);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        return new Error(`Error fetching lights presets: ${error}`);
    }
}

export { getDirectionalLight, getSpotLight, getAmbientLight, getThreeParamsFromPresets, getLightsPresetsSortByTypes, fetchLightsPresets };
